import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizeBookHtml } from './sanitizeBookHtml';

interface BookPreviewProps {
  bookHtml: string;
  onClose: () => void;
}

// Fallback page dimensions (6×9in at 96dpi) before actual measurement
const FALLBACK_W = 576;
const FALLBACK_H = 864;

export function BookPreview({ bookHtml, onClose }: BookPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [spread, setSpread] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [pageSize, setPageSize] = useState({ w: FALLBACK_W, h: FALLBACK_H });

  const totalSpreads = Math.ceil(pageCount / 2);
  const spreadW = pageSize.w * 2 + 8; // 8px spine gap between pages
  const spreadH = pageSize.h;

  // Build iframe document — PagedConfig.after must come before the polyfill script
  useEffect(() => {
    const safeBookHtml = sanitizeBookHtml(bookHtml);
    const parser = new DOMParser();
    const doc = parser.parseFromString(safeBookHtml, 'text/html');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <base href="${window.location.origin}/">
  <link rel="stylesheet" href="/book.css">
  <style>
    html, body { margin: 0; padding: 0; overflow: hidden; background: transparent; }
    .pagedjs_pages {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      gap: 8px !important;
      padding: 0 !important;
      margin: 0 !important;
      overflow: hidden !important;
    }
    .pagedjs_page {
      flex-shrink: 0 !important;
      box-shadow: 0 4px 24px rgba(0,0,0,0.35) !important;
    }
  </style>
  <script>
    window.PagedConfig = {
      auto: true,
      after: function (flow) {
        var pages = document.querySelectorAll('.pagedjs_page');
        var p = pages[0];
        window.parent.postMessage({
          type: 'pagedjs:ready',
          pageCount: flow.total,
          pageWidth: p ? p.offsetWidth : ${FALLBACK_W},
          pageHeight: p ? p.offsetHeight : ${FALLBACK_H}
        }, '*');
      }
    };
    window.addEventListener('message', function (event) {
      if (event.data?.type !== 'pagedjs:show-spread') return;
      var lo = event.data.spread * 2;
      document.querySelectorAll('.pagedjs_page').forEach(function (page, index) {
        page.style.display = (index === lo || index === lo + 1) ? '' : 'none';
      });
    });
  <\/script>
  <script src="https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js"><\/script>
</head>
<body>${doc.body.innerHTML}</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    blobUrlRef.current = URL.createObjectURL(blob);
    if (iframeRef.current) iframeRef.current.src = blobUrlRef.current;

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [bookHtml]);

  // Show only the two pages for the given spread index
  const showSpread = useCallback((idx: number) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'pagedjs:show-spread', spread: idx }, '*');
  }, []);

  // Handle ready signal from Paged.js inside the iframe
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type !== 'pagedjs:ready') return;
      setPageCount(e.data.pageCount);
      setPageSize({ w: e.data.pageWidth, h: e.data.pageHeight });
      setIsReady(true);
      showSpread(0);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [showSpread]);

  const goTo = useCallback((next: number) => {
    setSpread(next);
    showSpread(next);
  }, [showSpread]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight' && spread < totalSpreads - 1) goTo(spread + 1);
      if (e.key === 'ArrowLeft' && spread > 0) goTo(spread - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, spread, totalSpreads, goTo]);

  // Scale the spread to fill the available container
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isReady) return;
    const calc = () => {
      setScale(Math.min((el.clientWidth - 48) / spreadW, (el.clientHeight - 48) / spreadH));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isReady, spreadW, spreadH]);

  const leftPage = spread * 2 + 1;
  const rightPage = Math.min(spread * 2 + 2, pageCount);
  const pageLabel = leftPage === rightPage
    ? `Page ${leftPage} of ${pageCount}`
    : `Pages ${leftPage}–${rightPage} of ${pageCount}`;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-stone-900">
      {/* Navigation bar */}
      <div className="shrink-0 flex items-center justify-between px-5 py-2.5 bg-stone-800 border-b border-stone-700">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded text-sm font-medium text-stone-200 bg-stone-700 hover:bg-stone-600 transition-colors"
        >
          ✕ Close Preview
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => goTo(spread - 1)}
            disabled={spread === 0 || !isReady}
            aria-label="Previous pages"
            className="w-9 h-9 flex items-center justify-center rounded text-2xl text-white bg-stone-700 hover:bg-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors leading-none"
          >
            ‹
          </button>
          <span className="min-w-[160px] text-center text-sm text-stone-300 tabular-nums">
            {isReady ? pageLabel : 'Laying out pages…'}
          </span>
          <button
            type="button"
            onClick={() => goTo(spread + 1)}
            disabled={spread >= totalSpreads - 1 || !isReady}
            aria-label="Next pages"
            className="w-9 h-9 flex items-center justify-center rounded text-2xl text-white bg-stone-700 hover:bg-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors leading-none"
          >
            ›
          </button>
        </div>

        <span className="hidden sm:block text-xs text-stone-500 w-28 text-right select-none">
          ← → to navigate
        </span>
      </div>

      {/* Book spread area */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center overflow-hidden bg-stone-800">
        {!isReady && (
          <div className="flex items-center gap-2 text-stone-400 text-sm">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Laying out your book…
          </div>
        )}
        <div
          style={{
            width: spreadW,
            height: spreadH,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            visibility: isReady ? 'visible' : 'hidden',
          }}
        >
          <iframe
            ref={iframeRef}
            title="Book Preview"
            sandbox="allow-scripts"
            style={{ width: spreadW, height: spreadH, border: 'none', background: 'white', display: 'block' }}
          />
        </div>
      </div>
    </div>
  );
}
